// Global WebSocket service that persists across page navigation
import { tradingAgentApi } from './api';

class GlobalWebSocketService {
  constructor() {
    this.connections = new Map(); // Map of connectionKey -> connection info
    this.messageListeners = new Map(); // Map of connectionKey -> array of callback functions
    this.DEBUG_MODE = true;
  }

  debugLog(message, data) {
    if (!this.DEBUG_MODE) return;
    
    if (data) {
      console.log(`[GlobalWS] ${message}`, data);
    } else {
      console.log(`[GlobalWS] ${message}`);
    }
  }

  // Start a persistent WebSocket connection for an agent
  startConnection(agentId, externalId, strategyId, connectionType = 'logs') {
    if (!externalId || !agentId) {
      this.debugLog('Cannot start connection without agentId and externalId');
      return;
    }

    // Use both agentId and externalId to create unique connection key
    // This ensures each agent gets its own connection even if they share external_id
    const connectionKey = `${agentId}-${externalId}-${connectionType}`;
    
    // If already connected, don't create duplicate
    if (this.connections.has(connectionKey)) {
      const existing = this.connections.get(connectionKey);
      if (existing.ws && existing.ws.readyState === WebSocket.OPEN) {
        this.debugLog(`Connection already exists for ${connectionKey}`);
        return existing;
      } else {
        // Clean up dead connection
        this.stopConnection(agentId, externalId, connectionType);
      }
    }

    this.debugLog(`Starting persistent connection for agent ${agentId}, external ID: ${externalId}, type: ${connectionType}`);

    const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
    const hostname = 'token-ai-agent-d09010489214.herokuapp.com';
    const endpoint = connectionType === 'chat' ? 'agent_chat' : 'logs';
    const wsUrl = `${protocol}${hostname}/ws/${endpoint}/${externalId}`;

    const ws = new WebSocket(wsUrl);
    let reconnectTimeout = null;
    let keepaliveInterval = null;
    let messageCount = 0;

    const connectionInfo = {
      agentId,
      externalId,
      strategyId,
      connectionType,
      ws,
      reconnectTimeout,
      keepaliveInterval,
      messageCount: 0,
      lastActivity: Date.now()
    };

    ws.onopen = () => {
      this.debugLog(`Persistent connection opened for agent ${agentId}`);
      connectionInfo.lastActivity = Date.now();
      
      // Start keepalive
      keepaliveInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try {
            ws.send(JSON.stringify({ type: 'ping' }));
          } catch (e) {
            this.debugLog('Keepalive ping failed:', e);
          }
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      connectionInfo.messageCount++;
      connectionInfo.lastActivity = Date.now();
      
      this.debugLog(`Persistent connection received message #${connectionInfo.messageCount} for agent ${agentId}: ${event.data}`);
      
      // Skip ping/pong messages
      try {
        const parsed = JSON.parse(event.data);
        if (parsed.type === 'pong' || parsed.type === 'ping') {
          this.debugLog(`Skipping ping/pong message`);
          return;
        }
      } catch (e) {
        // Not JSON, continue processing
        this.debugLog(`Message is not JSON, processing as text`);
      }

      // Save EVERY message to database immediately with multiple retries
      // But don't interfere with UI - only save to database
      this.saveMessageToDatabase(agentId, event.data, strategyId, externalId, connectionInfo.connectionType).catch(error => {
        console.error(`[GlobalWS] Failed to save message #${connectionInfo.messageCount}, retrying...`, error);
        
        // Retry up to 3 times with increasing delays
        let retryCount = 0;
        const maxRetries = 3;
        
        const retryWithBackoff = () => {
          retryCount++;
          const delay = Math.min(1000 * Math.pow(2, retryCount - 1), 5000); // 1s, 2s, 4s max
          
          setTimeout(() => {
            this.saveMessageToDatabase(agentId, event.data, strategyId, externalId, connectionInfo.connectionType).catch(retryError => {
              console.error(`[GlobalWS] Retry ${retryCount} failed for message #${connectionInfo.messageCount}:`, retryError);
              
              if (retryCount < maxRetries) {
                retryWithBackoff();
              } else {
                console.error(`[GlobalWS] All retries failed for message #${connectionInfo.messageCount}`);
              }
            });
          }, delay);
        };
        
        retryWithBackoff();
      });
    };

    ws.onclose = (event) => {
      this.debugLog(`Persistent connection closed for agent ${agentId}, code: ${event.code}`);
      
      // Clear keepalive
      if (keepaliveInterval) {
        clearInterval(keepaliveInterval);
      }

      // Reconnect on abnormal closure
      if (event.code !== 1000 && event.code !== 1001) {
        this.debugLog(`Reconnecting persistent connection for agent ${agentId} in 5 seconds`);
        reconnectTimeout = setTimeout(() => {
          this.startConnection(agentId, externalId, strategyId, connectionType);
        }, 5000);
      }
    };

    ws.onerror = (error) => {
      this.debugLog(`Persistent connection error for agent ${agentId}:`, error);
    };

    // Store connection info
    connectionInfo.ws = ws;
    connectionInfo.reconnectTimeout = reconnectTimeout;
    connectionInfo.keepaliveInterval = keepaliveInterval;
    this.connections.set(connectionKey, connectionInfo);

    return connectionInfo;
  }

  // Save message to database with deduplication
  async saveMessageToDatabase(agentId, rawMessage, strategyId, externalId, connectionType = 'logs') {
    try {
      // Parse and format the message
      let formattedMessage;
      let messageData = null;
      
      try {
        if (typeof rawMessage === 'string' && (rawMessage.startsWith('{') || rawMessage.startsWith('['))) {
          const parsed = JSON.parse(rawMessage);
          
          // CRITICAL: Check if this is a final strategy result with data field
          if (parsed.data && parsed.message) {
            // This is a final strategy result - format it properly
            this.debugLog(`[GlobalWS] Detected final strategy result with feasibility data for agent ${agentId}`);
            messageData = parsed.data;
            
            // Format the complete strategy message
            formattedMessage = `I've analyzed your trading instruction.\n\n`;
            
            if (parsed.data.plan) {
              const plan = parsed.data.plan;
              if (plan.goal || plan.strategy_type) formattedMessage += `**Strategy:** ${plan.goal || plan.strategy_type}\n`;
              if (plan.token_address) formattedMessage += `**Token:** ${plan.token_address}\n`;
              if (plan.trade_direction) formattedMessage += `**Direction:** ${plan.trade_direction}\n`;
              if (plan.duration_days) formattedMessage += `**Duration:** ${plan.duration_days} days\n`;
              formattedMessage += `\n`;
            }
            
            formattedMessage += `${parsed.message}\n\n`;
            
            if (parsed.data.feasible) {
              formattedMessage += "✅ This strategy looks feasible and ready to execute.\n\nWould you like to execute this trading plan?";
            } else {
              formattedMessage += "⚠️ This strategy is not feasible. Please modify your instruction.\n\nPlease provide a new trading instruction and try again.";
            }
            
            // Also save the parsed strategy data to localStorage for the agent
            if (parsed.data.feasible && parsed.data.plan) {
              try {
                const strategyDetailsKey = `agent_strategy_${agentId}`;
                const storedDetails = localStorage.getItem(strategyDetailsKey) || '{}';
                const parsedDetails = JSON.parse(storedDetails);
                parsedDetails.parsedStrategy = parsed.data.plan;
                localStorage.setItem(strategyDetailsKey, JSON.stringify(parsedDetails));
                this.debugLog(`[GlobalWS] Saved parsed strategy to localStorage for agent ${agentId}`);
              } catch (e) {
                console.error('[GlobalWS] Error saving parsed strategy to localStorage:', e);
              }
            }
          }
          // Regular message extraction
          else if (parsed.message && typeof parsed.message === 'string') {
            const msg = parsed.message;
            
            // CRITICAL: Always save SUMMARY messages as they indicate strategy completion
            if (msg.includes('[SUMMARY]') || msg.includes('Trading plan completed') || msg.includes('Strategy completed') ||
                msg.includes('Execution completed') || msg.includes('Strategy finished') || msg.includes('Plan executed') ||
                msg.includes('All trades completed') || msg.includes('Strategy execution finished')) {
              this.debugLog(`[GlobalWS] CRITICAL: Detected strategy completion summary for agent ${agentId}`);
              formattedMessage = parsed.message;
              
              // Mark this as a terminal log since it's execution-related
              if (connectionType === 'chat') {
                this.debugLog(`[GlobalWS] Switching summary message to terminal log for agent ${agentId}`);
                connectionType = 'logs';
              }
              
              // AUTO-STOP: Automatically stop the strategy when it completes
              this.autoStopCompletedStrategy(agentId, strategyId, externalId).catch(error => {
                console.error(`[GlobalWS] Failed to auto-stop strategy for agent ${agentId}:`, error);
              });
            }
            // Filter out premature feasibility messages that don't have the data field
            else if (!parsed.data && (
              msg.includes('⚠️ This strategy is not feasible') || 
              msg.includes('✅ This strategy looks feasible') ||
              msg.includes('Would you like to execute this trading plan?') ||
              msg.includes("I've analyzed your trading instruction") ||
              msg.includes('Failed to parse response as JSON')
            )) {
              this.debugLog(`[GlobalWS] Filtered premature feasibility message for agent ${agentId}`);
              return; // Don't save premature messages
            }
            else {
              formattedMessage = parsed.message;
            }
          } else if (parsed.update && typeof parsed.update === 'string') {
            formattedMessage = parsed.update;
          } else if (parsed.text && typeof parsed.text === 'string') {
            formattedMessage = parsed.text;
          } else if (parsed.content && typeof parsed.content === 'string') {
            formattedMessage = parsed.content;
          } else if (parsed.response && typeof parsed.response === 'string') {
            formattedMessage = parsed.response;
          } else {
            // Show entire JSON object
            formattedMessage = JSON.stringify(parsed, null, 2);
          }
        } else {
          // Handle raw text messages - check for summary indicators
          const rawText = String(rawMessage);
          if (rawText.includes('[SUMMARY]') || rawText.includes('Trading plan completed') || rawText.includes('Strategy completed') ||
              rawText.includes('Execution completed') || rawText.includes('Strategy finished') || rawText.includes('Plan executed') ||
              rawText.includes('All trades completed') || rawText.includes('Strategy execution finished')) {
            this.debugLog(`[GlobalWS] CRITICAL: Detected raw summary message for agent ${agentId}`);
            formattedMessage = rawText;
            
            // Mark this as a terminal log since it's execution-related
            if (connectionType === 'chat') {
              this.debugLog(`[GlobalWS] Switching raw summary message to terminal log for agent ${agentId}`);
              connectionType = 'logs';
            }
            
            // AUTO-STOP: Automatically stop the strategy when it completes
            this.autoStopCompletedStrategy(agentId, strategyId, externalId).catch(error => {
              console.error(`[GlobalWS] Failed to auto-stop strategy for agent ${agentId}:`, error);
            });
          } else {
            formattedMessage = rawMessage;
          }
        }
      } catch (error) {
        formattedMessage = rawMessage; // Use raw message on parse error
      }

      this.debugLog(`Attempting to save message to database for agent ${agentId}: ${formattedMessage.substring(0, 100)}...`);

      let result;
      if (connectionType === 'chat') {
        // Save to agent_chats table for chat messages
        result = await tradingAgentApi.addAgentChatMessage(agentId, 'system', formattedMessage);
        this.debugLog(`Successfully saved chat message to database for agent ${agentId}`, result);
      } else {
        // Save to terminal_logs table for terminal/logs messages  
        result = await tradingAgentApi.addTerminalLog(
          agentId,
          formattedMessage,
          strategyId,
          externalId
        );
        this.debugLog(`Successfully saved terminal log to database for agent ${agentId}`, result);
      }

      return result;
    } catch (error) {
      this.debugLog(`Error saving message to database for agent ${agentId}:`, error);
      throw error; // Re-throw to trigger retry logic
    }
  }

  // Stop a persistent connection
  stopConnection(agentId, externalId, connectionType = 'logs') {
    const connectionKey = `${agentId}-${externalId}-${connectionType}`;
    const connectionInfo = this.connections.get(connectionKey);
    if (!connectionInfo) return;

    this.debugLog(`Stopping persistent connection for agent ${agentId}, external ID: ${externalId}, type: ${connectionType}`);

    // Clear timers
    if (connectionInfo.reconnectTimeout) {
      clearTimeout(connectionInfo.reconnectTimeout);
    }
    if (connectionInfo.keepaliveInterval) {
      clearInterval(connectionInfo.keepaliveInterval);
    }

    // Close WebSocket
    if (connectionInfo.ws) {
      try {
        connectionInfo.ws.close(1000, 'Stopping connection');
      } catch (e) {
        this.debugLog('Error closing WebSocket:', e);
      }
    }

    // Remove from connections
    this.connections.delete(connectionKey);
  }

  // Get connection status
  getConnectionStatus(agentId, externalId, connectionType = 'logs') {
    const connectionKey = `${agentId}-${externalId}-${connectionType}`;
    const connectionInfo = this.connections.get(connectionKey);
    if (!connectionInfo) return null;

    return {
      connected: connectionInfo.ws && connectionInfo.ws.readyState === WebSocket.OPEN,
      messageCount: connectionInfo.messageCount,
      lastActivity: connectionInfo.lastActivity,
      agentId: connectionInfo.agentId
    };
  }

  // Clean up inactive connections (call periodically)
  cleanupInactiveConnections() {
    const now = Date.now();
    const INACTIVE_THRESHOLD = 10 * 60 * 1000; // 10 minutes

    for (const [connectionKey, connectionInfo] of this.connections.entries()) {
      if (now - connectionInfo.lastActivity > INACTIVE_THRESHOLD) {
        this.debugLog(`Cleaning up inactive connection: ${connectionKey}`);
        // Extract agent ID, external ID and connection type from key
        const keyParts = connectionKey.split('-');
        const agentId = keyParts[0];
        const externalId = keyParts.slice(1, -1).join('-'); // Handle external IDs that might contain hyphens
        const connectionType = keyParts[keyParts.length - 1];
        this.stopConnection(agentId, externalId, connectionType);
      }
    }
  }

  // Stop all connections (for cleanup)
  stopAllConnections() {
    this.debugLog('Stopping all persistent connections');
    for (const [connectionKey] of this.connections.entries()) {
      const keyParts = connectionKey.split('-');
      const agentId = keyParts[0];
      const externalId = keyParts.slice(1, -1).join('-'); // Handle external IDs that might contain hyphens
      const connectionType = keyParts[keyParts.length - 1];
      this.stopConnection(agentId, externalId, connectionType);
    }
  }

  // Automatically stop a strategy when completion is detected
  async autoStopCompletedStrategy(agentId, strategyId, externalId) {
    this.debugLog(`[AUTO-STOP] Attempting to auto-stop completed strategy for agent ${agentId}`);
    
    try {
      // First, get the agent details to find the active strategy
      const agentDetails = await tradingAgentApi.getAgentById(agentId);
      
      if (!agentDetails) {
        this.debugLog(`[AUTO-STOP] Agent ${agentId} not found`);
        return;
      }
      
      // Find an active strategy for this agent
      let activeStrategyId = strategyId;
      if (!activeStrategyId && agentDetails.strategies) {
        const activeStrategy = agentDetails.strategies.find(s => 
          s.status === 'active' || s.status === 'running'
        );
        activeStrategyId = activeStrategy ? activeStrategy.id : null;
      }
      
      if (!activeStrategyId) {
        this.debugLog(`[AUTO-STOP] No active strategy found for agent ${agentId}`);
        return;
      }
      
      this.debugLog(`[AUTO-STOP] Stopping strategy ${activeStrategyId} for agent ${agentId}`);
      
      // Call the API to stop the strategy
      const stopResult = await tradingAgentApi.stopStrategy(agentId, activeStrategyId);
      
      this.debugLog(`[AUTO-STOP] Successfully stopped strategy ${activeStrategyId} for agent ${agentId}`, stopResult);
      
      // Add a terminal log to indicate auto-stop
      await tradingAgentApi.addTerminalLog(
        agentId,
        `🤖 [AUTO-STOP] Strategy automatically stopped after completion detection`,
        activeStrategyId,
        externalId
      );
      
      this.debugLog(`[AUTO-STOP] Added auto-stop log for agent ${agentId}`);
      
      // CRITICAL: Trigger UI updates to reflect the stopped state
      this.triggerUIUpdatesAfterAutoStop(agentId, activeStrategyId);
      
      return { success: true, strategyId: activeStrategyId, agentId };
      
    } catch (error) {
      this.debugLog(`[AUTO-STOP] Error stopping strategy for agent ${agentId}:`, error);
      
      // Try to add an error log
      try {
        await tradingAgentApi.addTerminalLog(
          agentId,
          `❌ [AUTO-STOP] Failed to automatically stop strategy: ${error.message}`,
          strategyId,
          externalId
        );
      } catch (logError) {
        console.error(`[AUTO-STOP] Failed to log auto-stop error:`, logError);
      }
      
      throw error; // Re-throw so calling code can handle
    }
  }

  // Trigger UI updates after auto-stop to reflect the new state
  triggerUIUpdatesAfterAutoStop(agentId, strategyId) {
    this.debugLog(`[AUTO-STOP] Triggering UI updates for agent ${agentId}, strategy ${strategyId}`);
    
    try {
      // 1. Dispatch custom events that components can listen to
      const autoStopEvent = new CustomEvent('strategy-auto-stopped', {
        detail: { agentId, strategyId }
      });
      window.dispatchEvent(autoStopEvent);
      
      // 2. Send WebSocket message to all connections for this agent
      const updateMessage = {
        type: 'strategy_status_update',
        agentId: agentId,
        strategyId: strategyId,
        status: 'stopped',
        autoStopped: true,
        timestamp: new Date().toISOString()
      };
      
      // Broadcast to all agent connections
      this.broadcastToAllAgentConnections(agentId, updateMessage);
      
      this.debugLog(`[AUTO-STOP] UI update triggers sent for agent ${agentId}`);
      
    } catch (error) {
      console.error(`[AUTO-STOP] Error triggering UI updates for agent ${agentId}:`, error);
    }
  }

  // Broadcast a message to all connection types for an agent
  broadcastToAllAgentConnections(agentId, message) {
    const connectionTypes = ['chat', 'logs'];
    let totalSent = 0;
    
    connectionTypes.forEach(type => {
      for (const [connectionKey, connectionInfo] of this.connections.entries()) {
        if (connectionKey.startsWith(`${agentId}-`) && connectionKey.endsWith(`-${type}`)) {
          if (connectionInfo.ws && connectionInfo.ws.readyState === WebSocket.OPEN) {
            try {
              connectionInfo.ws.send(JSON.stringify(message));
              totalSent++;
            } catch (error) {
              console.error(`[GlobalWS] Error broadcasting to ${connectionKey}:`, error);
            }
          }
        }
      }
    });
    
    this.debugLog(`[AUTO-STOP] Broadcast update to ${totalSent} connections for agent ${agentId}`);
    return totalSent;
  }
}

// Create singleton instance
const globalWebSocketService = new GlobalWebSocketService();

// Cleanup inactive connections every 5 minutes
setInterval(() => {
  globalWebSocketService.cleanupInactiveConnections();
}, 5 * 60 * 1000);

// Don't automatically close connections on page unload - let them persist
// This allows messages to be captured even when user navigates away
// Connections will be cleaned up by the inactive connection cleanup or when strategies stop

export default globalWebSocketService;